import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SkinTips from '../SkinTips';
import UnifiedPDFPreview from './UnifiedPDFPreview';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getAnalyticsSessionId } from '../../utils/analytics';

const getSeverity = (pct) => {
  if (pct <= 30) return { label: 'Low', color: '#22c55e' };
  if (pct <= 70) return { label: 'Moderate', color: '#f59e0b' };
  return { label: 'High', color: '#ef4444' };
};

const CONDITIONS = {
  darkCircles: 'Dark Circles',
  eyeBags: 'Eye Bags',
  wrinkles: 'Fine Lines',
  deepWrinkles: 'Deep Wrinkles',
  pores: 'Pores',
  pigmentation: 'Pigmentation',
  acne: 'Acne',
  eyeWrinkles: "Crow's Feet"
};

const USER_CONCERN_LABELS = {
  acne: 'Acne',
  dull_skin: 'Dull Skin',
  pigmentation: 'Pigmentation',
  acne_scar: 'Acne Scars',
  open_pores: 'Open Pores',
  normal_skin: 'General Checkup',
  uneven_skin_tone: 'Uneven Skin Tone',
  wrinkles: 'Wrinkles',
  loose_skin: 'Loose Skin',
  dark_circles: 'Dark Circles'
};

const ANNOTATION_REQUEST_TIMEOUT_MS = 70000;
const ANNOTATION_ABORT_AFTER_MS = 45000;
const PDF_RENDER_SCALE = 2;
const ADITYA_PDF_RENDER_SCALE = 1.2;
const ADITYA_PDF_JPEG_QUALITY = 0.7;

const buildPdfFilename = name => {
  const safeName = String(name || 'Report')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '');
  return `SkinAnalysis_${safeName || 'Report'}.pdf`;
};

function demotePores(sortedConditions) {
  const idx = sortedConditions.findIndex(([k]) => k === 'pores');
  if (idx >= 0 && idx < 4) {
    const [entry] = sortedConditions.splice(idx, 1);
    sortedConditions.splice(4, 0, entry);
  }
  return sortedConditions;
}

const withAlpha = (hex, alpha) => {
  if (!hex) return hex;
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  return `${hex}${alpha}`;
};

const UnifiedDoctorAnalysis = ({ nextStep, previousStep, updateAnalysisResult, appState, setError, doctorInfo, config }) => {
  const doctor = config || doctorInfo || {};
  const accent = doctor.analysisAccent || doctor.accentColor || '#B32C29';
  const accentDark = doctor.analysisAccentDark || accent;
  const accentLight = doctor.analysisAccentLight || accent;
  const timerColor = doctor.analysisTimerColor || accent;
  const routineTheme =
    doctor.routineTheme ||
    {
      card: 'bg-gradient-to-r from-purple-50 to-pink-50',
      iconBg: 'bg-purple-100',
      iconText: 'text-purple-600',
      titleText: 'text-purple-900',
      bodyText: 'text-purple-800'
    };

  const [state, setState] = useState({
    analyzing: false,
    progress: 0,
    phase: 'init',
    result: appState.analysisResult || null,
    annotatedImage: null,
    annotationLoading: false,
    annotationError: null,
    annotationTimer: 0,
    annotationDone: false,
    error: null,
    retries: 0,
    elapsed: 0
  });

  const [pdf, setPdf] = useState({ generating: false, ready: false, url: null, downloaded: false, filename: null });
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [activeTab, setActiveTab] = useState('results');
  const pdfRef = useRef(null);
  const startedRef = useRef(false);
  const isAndroidDevice = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

  useEffect(() => {
    if (!state.result && appState.capturedImage && appState.patientData && !state.analyzing && !startedRef.current) {
      startedRef.current = true;
      setTimeout(analyze, 300);
    }
  }, [state.result, appState.capturedImage, appState.patientData]);

  useEffect(() => () => pdf.url && URL.revokeObjectURL(pdf.url), [pdf.url]);

  useEffect(() => {
    if (state.annotationDone && state.result && !pdf.generating && !pdf.ready) {
      createPDF();
    }
  }, [state.annotationDone]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (viewingImage) {
        e.preventDefault();
        setViewingImage(null);
      } else if (selectedCondition) {
        e.preventDefault();
        setSelectedCondition(null);
      }
    };

    if (viewingImage || selectedCondition) {
      window.history.pushState({ modal: true }, '');
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [viewingImage, selectedCondition]);

  const analyze = async () => {
    if (state.analyzing || !appState.capturedImage || !appState.patientData) return;
    setState(s => ({ ...s, analyzing: true, error: null, progress: 0, phase: 'init', elapsed: 0 }));

    const start = Date.now();
    const timer = setInterval(() => {
      const ms = Date.now() - start;
      const sec = Math.floor(ms / 1000);
      const prog = Math.min(95, (ms / 150000) * 95);
      const phase = sec < 15 ? 'scanning' : sec < 45 ? 'analyzing' : sec < 90 ? 'processing' : 'finalizing';
      setState(s => ({ ...s, progress: prog, phase, elapsed: sec }));
    }, 250);

    try {
      const sessionId = getAnalyticsSessionId();
      const res = await axios.post(
        '/api/skin-analysis',
        {
          imageData: appState.capturedImage.base64,
          userInfo: {
            name: appState.patientData.name,
            age: appState.patientData.age,
            gender: appState.patientData.gender,
            phone: appState.patientData.phone,
            city: appState.patientData.city,
            state: appState.patientData.state,
            primaryConcern: appState.patientData.primaryConcern,
            visitPurpose: appState.patientData.visitPurpose,
            conditionDuration: appState.patientData.conditionDuration
          },
          doctorSlug: doctor.slug || doctorInfo?.slug
        },
        {
          timeout: 290000,
          headers: {
            'x-session-id': sessionId
          }
        }
      );

      clearInterval(timer);

      if (res.data.success) {
        const result = {
          ...res.data.data,
          annotationData: res.data.annotationData,
          timestamp: new Date().toISOString(),
          patientId: `${appState.patientData.name}_${Date.now()}`
        };
        setState(s => ({ ...s, analyzing: false, progress: 100, result, phase: 'done' }));
        updateAnalysisResult(result);
        fetchAnnotatedImage(res.data.annotationData);
      } else {
        throw new Error(res.data.error || 'Analysis failed');
      }
    } catch (err) {
      clearInterval(timer);
      setState(s => ({ ...s, analyzing: false, error: err.response?.data?.error || err.message, retries: s.retries + 1, progress: 0 }));
    }
  };

  const fetchAnnotatedImage = async (annotationData) => {
    if (!annotationData) {
      setState(s => ({ ...s, annotatedImage: null, annotationLoading: false, annotationError: 'Annotation unavailable', annotationTimer: 0, annotationDone: true }));
      return;
    }

    setState(s => ({ ...s, annotationLoading: true, annotationError: null, annotationTimer: 0 }));

    const timerInterval = setInterval(() => {
      setState(s => ({ ...s, annotationTimer: s.annotationTimer + 1 }));
    }, 1000);

    const controller = new AbortController();
    let abortedByDeadline = false;
    const deadline = setTimeout(() => {
      abortedByDeadline = true;
      controller.abort();
    }, ANNOTATION_ABORT_AFTER_MS);

    try {
      const res = await axios.post(
        '/api/generate-annotated-image',
        {
          originalImage: appState.capturedImage.base64,
          annotationData,
          doctorSlug: doctor.slug || doctorInfo?.slug
        },
        {
          timeout: ANNOTATION_REQUEST_TIMEOUT_MS,
          signal: controller.signal
        }
      );

      if (res.data.success && res.data.annotatedImage) {
        setState(s => ({ ...s, annotatedImage: res.data.annotatedImage, annotationLoading: false, annotationTimer: 0, annotationDone: true }));
      } else {
        setState(s => ({ ...s, annotatedImage: null, annotationLoading: false, annotationError: res.data.error || 'Failed', annotationTimer: 0, annotationDone: true }));
      }
    } catch (err) {
      const canceled = abortedByDeadline || err?.code === 'ERR_CANCELED' || String(err?.message || '').toLowerCase().includes('canceled');
      const errorMessage = canceled ? 'Annotation timed out. Continuing with original image for PDF.' : err.message;
      setState(s => ({ ...s, annotatedImage: null, annotationLoading: false, annotationError: errorMessage, annotationTimer: 0, annotationDone: true }));
    } finally {
      clearInterval(timerInterval);
      clearTimeout(deadline);
    }
  };

  const retry = () => {
    if (state.retries < 3) {
      startedRef.current = false;
      setState(s => ({ ...s, error: null }));
      setTimeout(() => {
        startedRef.current = true;
        analyze();
      }, 100);
    }
  };

  const waitForPdfAssets = async (rootEl) => {
    if (!rootEl) return;

    const images = Array.from(rootEl.querySelectorAll('img'));
    await Promise.all(
      images.map(
        img =>
          new Promise(resolve => {
            let settled = false;
            const done = () => {
              if (settled) return;
              settled = true;
              img.removeEventListener('load', done);
              img.removeEventListener('error', done);
              resolve();
            };

            if (img.complete && img.naturalWidth > 0) {
              if (typeof img.decode === 'function') {
                img.decode().then(done).catch(done);
              } else {
                done();
              }
              return;
            }

            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
            setTimeout(done, 3000);
          })
      )
    );

    if (document?.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {
        // ignore font readiness failures
      }
    }

    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  };

  const blobToDataUri = blob =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('pdf_blob_read_failed'));
      reader.readAsDataURL(blob);
    });

  const sendWhatsAppReport = async (pdfBlob) => {
    const slug = String(doctor.slug || doctorInfo?.slug || '').toLowerCase();
    if (slug !== 'adityashah') return;

    try {
      const sessionId = getAnalyticsSessionId();
      const pdfData = await blobToDataUri(pdfBlob);

      await axios.post(
        '/api/send-whatsapp-report',
        {
          doctorSlug: slug,
          userInfo: {
            name: appState.patientData?.name,
            phone: appState.patientData?.phone
          },
          pdfData,
          pdfFilename: `SkinAnalysis_${appState.patientData?.name || 'Report'}.pdf`
        },
        {
          timeout: 180000,
          headers: {
            'x-session-id': sessionId
          }
        }
      );
    } catch (error) {
      // Keep UI uninterrupted if WhatsApp report delivery fails.
    }
  };

  const createPDF = async () => {
    if (!state.result || !appState.patientData || pdf.generating || pdf.ready) return;
    setPdf(p => ({ ...p, generating: true }));

    try {
      await new Promise(r => setTimeout(r, 500));
      if (!pdfRef.current) throw new Error('PDF not ready');
      await waitForPdfAssets(pdfRef.current);
      const doctorSlug = String(doctor.slug || doctorInfo?.slug || '').toLowerCase();
      const isAdityaShah = doctorSlug === 'adityashah';
      const renderScale = isAdityaShah ? ADITYA_PDF_RENDER_SCALE : PDF_RENDER_SCALE;
      const imageFormat = isAdityaShah ? 'JPEG' : 'PNG';
      const imageMimeType = isAdityaShah ? 'image/jpeg' : 'image/png';
      const pdfDoc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: isAdityaShah,
        precision: isAdityaShah ? 12 : 16
      });
      const pages = pdfRef.current.querySelectorAll('[data-pdf-page]');

      for (let i = 0; i < pages.length; i += 1) {
        await waitForPdfAssets(pages[i]);
        const pageCanvas = await html2canvas(pages[i], {
          scale: renderScale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#fff',
          width: 794,
          height: 1123,
          logging: false
        });

        const pageDataUrl = isAdityaShah
          ? pageCanvas.toDataURL(imageMimeType, ADITYA_PDF_JPEG_QUALITY)
          : pageCanvas.toDataURL(imageMimeType);

        if (i > 0) pdfDoc.addPage();
        pdfDoc.addImage(pageDataUrl, imageFormat, 0, 0, 210, 297);
      }

      const blob = pdfDoc.output('blob');
      setPdf({
        generating: false,
        ready: true,
        url: URL.createObjectURL(blob),
        downloaded: false,
        filename: buildPdfFilename(appState.patientData?.name)
      });
      updateAnalysisResult({ ...state.result, pdfBlob: blob });
      void sendWhatsAppReport(blob);
    } catch (err) {
      setPdf(p => ({ ...p, generating: false }));
      setError('PDF generation failed');
    }
  };

  const downloadPDF = () => {
    if (!pdf.url || pdf.downloaded) return;

    try {
      const link = document.createElement('a');
      link.href = pdf.url;
      link.download = pdf.filename || buildPdfFilename(appState.patientData?.name);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setPdf(prev => ({ ...prev, downloaded: true }));
    } catch (err) {
      setError('Failed to start PDF download');
    }
  };

  const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const scanLineStyle = doctor.analysisScanGradient
    ? { background: `linear-gradient(to right, ${accent}, ${accentDark})` }
    : { backgroundColor: accent };

  const progressStyle = doctor.analysisProgressGradient
    ? { background: `linear-gradient(to right, ${accent}, ${accentDark})`, width: `${state.progress}%` }
    : { backgroundColor: accent, width: `${state.progress}%` };

  const tabActiveStyle = doctor.analysisProgressGradient
    ? { background: `linear-gradient(to right, ${accent}, ${accentDark})` }
    : { backgroundColor: accent };
  const waitingLogoClass = doctor.analysisWaitingLogoClass || (doctor.slug === 'apratimgoel' ? 'h-24 mb-8' : 'h-48 mb-8');

  if (state.analyzing) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <img src={doctor.logoUrl} alt={doctor.logoAlt} className={waitingLogoClass} onError={e => { e.target.style.display = 'none'; }} />

        <div className="relative w-64 aspect-[3/4] rounded-2xl overflow-hidden mb-8 shadow-lg">
          <img src={appState.capturedImage?.dataUrl} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 border-2 rounded-2xl" style={{ borderColor: accent }} />
          <div className="absolute left-0 right-0 h-0.5 animate-scan" style={scanLineStyle} />
        </div>

        <div className="w-64 mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">
              {state.phase === 'init' && 'Initializing...'}
              {state.phase === 'scanning' && 'Scanning...'}
              {state.phase === 'analyzing' && 'Analyzing...'}
              {state.phase === 'processing' && 'Processing...'}
              {state.phase === 'finalizing' && 'Finalizing...'}
            </span>
            <span className="text-gray-400">{fmt(state.elapsed)}</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-300" style={progressStyle} />
          </div>
        </div>

        <p className="text-gray-400 text-sm">{Math.round(state.progress)}% complete</p>

        <div className="mt-8">
          <SkinTips />
        </div>

        <style>{`
          @keyframes scan { 0%, 100% { top: 0; } 50% { top: calc(100% - 2px); } }
          .animate-scan { animation: scan 2s ease-in-out infinite; }
          @keyframes wave { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          .animate-wave { animation: wave 2s linear infinite; }
          @keyframes bubble1 { 0% { bottom: 0; opacity: 0; } 20% { opacity: 0.6; } 100% { bottom: 100%; opacity: 0; } }
          @keyframes bubble2 { 0% { bottom: 0; opacity: 0; } 30% { opacity: 0.5; } 100% { bottom: 100%; opacity: 0; } }
          @keyframes bubble3 { 0% { bottom: 0; opacity: 0; } 25% { opacity: 0.7; } 100% { bottom: 100%; opacity: 0; } }
          .animate-bubble-1 { animation: bubble1 2.5s ease-out infinite; }
          .animate-bubble-2 { animation: bubble2 3s ease-out infinite 0.5s; }
          .animate-bubble-3 { animation: bubble3 2s ease-out infinite 1s; }
        `}</style>
      </div>
    );
  }

  if (state.error && !state.result) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
            <span className="text-2xl">!</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Analysis Failed</h2>
          <p className="text-gray-500 text-sm mb-6">{state.error}</p>
          {state.retries < 3 ? (
            <button
              onClick={retry}
              className="w-full text-white py-3 rounded-xl font-medium"
              style={doctor.analysisProgressGradient ? { background: `linear-gradient(to right, ${accent}, ${accentDark})` } : { backgroundColor: accent }}
            >
              Try Again
            </button>
          ) : (
            <button onClick={previousStep} className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium">
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  if (state.result) {
    const r = state.result;
    const conditions = demotePores(
      Object.entries(r.detailedAnalysis || {})
        .filter(([k]) => CONDITIONS[k])
        .sort((a, b) => (b[1].percentage || 0) - (a[1].percentage || 0))
    );

    const ctaHref = doctor.contactType === 'booking' ? doctor.bookingUrl : doctor.whatsappUrl;
    const ctaText = doctor.contactType === 'booking' ? 'Book Appointment' : 'Book Now';

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b px-4 py-4 sticky top-0 z-30">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <div>
              <h1 className="font-semibold text-gray-900">Analysis Results</h1>
              <p className="text-xs text-gray-400">{appState.patientData?.name} • {new Date().toLocaleDateString()}</p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                r.overallAssessment?.severity === 'Low'
                  ? 'bg-green-50 text-green-600'
                  : r.overallAssessment?.severity === 'High'
                    ? 'bg-red-50 text-red-600'
                    : 'bg-amber-50 text-amber-600'
              }`}
            >
              {r.overallAssessment?.severity || 'Moderate'}
            </span>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-6 pb-32">
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button onClick={() => setViewingImage('original')} className="bg-white rounded-xl p-3 shadow-sm">
              <p className="text-xs text-gray-400 mb-2">Original</p>
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img src={appState.capturedImage?.dataUrl} alt="" className="w-full h-full object-cover" />
              </div>
            </button>

            <button onClick={() => state.annotatedImage && setViewingImage('annotated')} className="bg-white rounded-xl p-3 shadow-sm">
              <p className="text-xs mb-2 font-medium" style={{ color: accent }}>
                AI Analysis
              </p>
              <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-b from-red-50 to-white relative">
                {state.annotationLoading ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="relative w-20 h-24 mb-3">
                      <div className="absolute inset-0 border-2 rounded-b-2xl rounded-t-lg" style={{ borderColor: withAlpha(accent, '4D') }} />

                      <div
                        className="absolute bottom-0 left-0 right-0 rounded-b-xl transition-all duration-1000 ease-out"
                        style={{
                          background: `linear-gradient(to top, ${accentDark}, ${withAlpha(accent, 'CC')}, ${withAlpha(accentLight, '66')})`,
                          height: `${Math.min(95, (state.annotationTimer / 25) * 100)}%`
                        }}
                      >
                        <div className="absolute -top-2 left-0 right-0 h-4 overflow-hidden">
                          <div className="animate-wave absolute w-[200%] h-full">
                            <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="h-full w-full" style={{ fill: withAlpha(accent, '99') }}>
                              <path d="M0,60 C150,120 350,0 600,60 C850,120 1050,0 1200,60 L1200,120 L0,120 Z" />
                            </svg>
                          </div>
                        </div>

                        <div className="absolute inset-0 overflow-hidden">
                          <div className="absolute w-2 h-2 bg-white/40 rounded-full animate-bubble-1 left-[20%]" />
                          <div className="absolute w-1.5 h-1.5 bg-white/30 rounded-full animate-bubble-2 left-[50%]" />
                          <div className="absolute w-1 h-1 bg-white/50 rounded-full animate-bubble-3 left-[70%]" />
                        </div>
                      </div>

                      <div className="absolute inset-0 flex items-center justify-center">
                        <span
                          className="text-lg font-bold text-white drop-shadow-md"
                          style={{ color: state.annotationTimer > 12 ? '#fff' : timerColor }}
                        >
                          {Math.min(95, Math.round((state.annotationTimer / 25) * 100))}%
                        </span>
                      </div>
                    </div>
                    <p className="text-sm font-medium" style={{ color: accent }}>
                      {state.annotationTimer < 8 ? 'Analyzing...' : state.annotationTimer < 16 ? 'Processing...' : state.annotationTimer < 22 ? 'Almost done...' : 'Finalizing...'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{state.annotationTimer}s</p>
                  </div>
                ) : state.annotatedImage ? (
                  <img src={state.annotatedImage} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400 p-2 text-center">{state.annotationError || 'Unavailable'}</div>
                )}
              </div>
            </button>
          </div>

          {r.ageVerification && !r.ageVerification.isAccurate ? (
            <div className="bg-gradient-to-r from-red-100 to-red-50 border-2 border-red-300 rounded-xl p-4 mb-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-700 mb-1">Age Verification Warning</p>
                  <p className="text-sm text-red-600">
                    {r.ageVerification.warningMessage ||
                      `The provided age (${r.ageVerification.providedAge}) may not match your apparent age (estimated ${r.ageVerification.estimatedAgeRange?.min}-${r.ageVerification.estimatedAgeRange?.max} years). This could affect the accuracy of your analysis.`}
                  </p>
                  <p className="text-xs text-red-500 mt-2 italic">AI Confidence: {r.ageVerification.confidence || 'medium'}</p>
                </div>
              </div>
            </div>
          ) : null}

          {appState.patientData?.primaryConcern && appState.patientData.primaryConcern !== 'normal_skin' ? (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-3">
              <p className="text-xs text-blue-500 font-medium uppercase tracking-wide mb-1">Patient Concern</p>
              <h3 className="text-lg font-bold text-gray-900">{USER_CONCERN_LABELS[appState.patientData.primaryConcern] || appState.patientData.primaryConcern}</h3>
            </div>
          ) : null}

          {conditions[0] && conditions[0][1].percentage > 15 ? (
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-100 rounded-xl p-4 mb-3">
              <p className="text-xs text-red-500 font-medium uppercase tracking-wide mb-1">AI-Identified Primary Concern</p>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">{CONDITIONS[conditions[0][0]]}</h3>
                <span className="text-2xl font-bold text-red-500">{conditions[0][1].percentage}%</span>
              </div>
              {conditions[0][1].description ? <p className="text-sm text-gray-600 mt-2">{conditions[0][1].description}</p> : null}
            </div>
          ) : null}

          {appState.patientData?.primaryConcern &&
          appState.patientData.primaryConcern !== 'normal_skin' &&
          conditions[0] &&
          !conditions[0][0].toLowerCase().includes(appState.patientData.primaryConcern.replace(/_/g, '')) ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6 text-sm text-amber-700">
              Note: Your stated concern differs from the AI-identified primary concern. Both have been noted for your consultation.
            </div>
          ) : null}

          <div className="flex bg-white rounded-lg p-1 mb-6 shadow-sm">
            {['results', 'routine', 'info'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab ? 'text-white' : 'text-gray-500'}`}
                style={activeTab === tab ? tabActiveStyle : undefined}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {activeTab === 'results' ? (
            <div className="space-y-3">
              {conditions.map(([key, data]) => {
                const sev = getSeverity(data.percentage || 0);
                return (
                  <button key={key} onClick={() => setSelectedCondition({ key, data })} className="w-full bg-white rounded-xl p-4 shadow-sm text-left flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{CONDITIONS[key]}</span>
                        <span className="font-semibold" style={{ color: sev.color }}>
                          {data.percentage}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${data.percentage}%`, backgroundColor: sev.color }} />
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })}
            </div>
          ) : null}

          {activeTab === 'routine' ? (
            <div className="space-y-4">
              {r.recommendations?.skincare ? (
                <div className={`${routineTheme.card} rounded-xl p-4 shadow-sm`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 ${routineTheme.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <svg className={`w-4 h-4 ${routineTheme.iconText}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-medium ${routineTheme.titleText} mb-2`}>Personalized Skincare Plan</h4>
                      <p className={`${routineTheme.bodyText} text-sm leading-relaxed whitespace-pre-line`}>{r.recommendations.skincare}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <h4 className="font-medium text-gray-900 mb-3">Morning Routine</h4>
                    {['Gentle cleanser', 'Vitamin C serum', 'Moisturizer', 'SPF 30+ sunscreen'].map((step, i) => (
                      <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0 border-gray-50">
                        <span className="w-6 h-6 rounded-full bg-amber-50 text-amber-600 text-xs font-medium flex items-center justify-center">{i + 1}</span>
                        <span className="text-gray-700 text-sm">{step}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <h4 className="font-medium text-gray-900 mb-3">Evening Routine</h4>
                    {['Oil cleanser', 'Water cleanser', 'Treatment serum', 'Night moisturizer'].map((step, i) => (
                      <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0 border-gray-50">
                        <span className="w-6 h-6 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium flex items-center justify-center">{i + 1}</span>
                        <span className="text-gray-700 text-sm">{step}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {r.recommendations?.followup ? (
                <div className="bg-orange-50 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-orange-900 mb-2">Professional Follow-up</h4>
                      <p className="text-orange-800 text-sm leading-relaxed">{r.recommendations.followup}</p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {activeTab === 'info' ? (
            <div className="space-y-4">
              {r.overallAssessment ? (
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <h4 className="font-medium text-gray-900 mb-3">Professional Assessment</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Overall Condition</p>
                      <p className="text-gray-900 text-sm">{r.overallAssessment.condition}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-400">Severity Level</p>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          r.overallAssessment.severity === 'Low'
                            ? 'bg-green-100 text-green-800'
                            : r.overallAssessment.severity === 'High'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {r.overallAssessment.severity}
                      </span>
                    </div>
                    {r.overallAssessment.skinAge ? (
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Skin Age Estimate</p>
                        <p className="text-gray-900 text-sm">{r.overallAssessment.skinAge}</p>
                      </div>
                    ) : null}
                    {r.overallAssessment.primaryConcerns && r.overallAssessment.primaryConcerns.length > 0 ? (
                      <div>
                        <p className="text-xs text-gray-400 mb-2">Primary Concerns</p>
                        <div className="flex flex-wrap gap-2">
                          {r.overallAssessment.primaryConcerns.map((concern, i) => (
                            <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                              {concern}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h4 className="font-medium text-gray-900 mb-3">Skin Profile</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center py-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-400">Type</p>
                    <p className="font-medium text-gray-900">{r.patientAnalysis?.skinType || 'Combination'}</p>
                  </div>
                  <div className="text-center py-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-400">Sensitivity</p>
                    <p className="font-medium text-gray-900">{r.patientAnalysis?.skinSensitivity || 'Low'}</p>
                  </div>
                </div>
              </div>

              {r.summary ? (
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{r.summary}</p>
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs text-blue-700">
                      <span className="font-medium">Age-Adjusted Analysis:</span> Results are calibrated for a {appState.patientData?.age || 25}-year-old. Severity thresholds are adjusted based on age-appropriate expectations.
                    </p>
                  </div>
                </div>
              ) : null}

              <div className="bg-gray-50 border-l-4 rounded-xl p-4" style={{ borderColor: accent }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4" style={{ color: accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1 text-sm">Medical Disclaimer</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">{r.disclaimer || doctor.analysisDisclaimerFallback}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
          <div className="max-w-lg mx-auto">
            {pdf.ready ? (
              <div
                className={`mb-3 rounded-xl border px-4 py-3 text-xs leading-relaxed ${
                  pdf.downloaded
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-amber-200 bg-amber-50 text-amber-800'
                }`}
              >
                {pdf.downloaded ? (
                  <>
                    <span className="font-semibold">PDF downloaded.</span> Check your Downloads folder{isAndroidDevice ? ' in the Files app' : ''} to open{' '}
                    {pdf.filename || 'your report'}. This button is now locked so the same report is not downloaded twice.
                  </>
                ) : (
                  <>
                    <span className="font-semibold">Download note:</span> Tap <span className="font-semibold">Download PDF</span> once. Your report will be
                    saved to the Downloads folder{isAndroidDevice ? ' on Android, and it may not open automatically' : ''}.
                  </>
                )}
              </div>
            ) : null}

            <div className="flex gap-3">
              {pdf.ready ? (
                <button
                  type="button"
                  onClick={downloadPDF}
                  disabled={pdf.downloaded}
                  className={`flex-1 py-3 rounded-xl font-medium text-center flex items-center justify-center gap-2 ${
                    pdf.downloaded ? 'bg-green-100 text-green-700 border border-green-200 cursor-not-allowed' : 'bg-green-500 text-white'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {pdf.downloaded ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    )}
                  </svg>
                  {pdf.downloaded ? 'PDF Downloaded' : 'Download PDF'}
                </button>
              ) : (
                <div className="flex-1 bg-blue-500 text-white py-3 rounded-xl font-medium text-center flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {pdf.generating ? 'Creating PDF...' : 'Preparing PDF...'}
                </div>
              )}
              <a href={ctaHref || '#'} target="_blank" rel="noopener noreferrer" className="flex-1 bg-[#25D366] text-white py-3 rounded-xl font-medium text-center">
                {ctaText}
              </a>
            </div>
          </div>
        </div>

        {viewingImage ? (
          <div className="fixed inset-0 z-50 bg-black flex items-center justify-center p-4" onClick={() => setViewingImage(null)}>
            <button className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full text-white">×</button>
            <img src={viewingImage === 'original' ? appState.capturedImage?.dataUrl : state.annotatedImage} alt="" className="max-w-full max-h-full object-contain" />
          </div>
        ) : null}

        {selectedCondition ? (
          <div className="fixed inset-0 z-50 flex items-end bg-black/30" onClick={() => setSelectedCondition(null)}>
            <div className="bg-white w-full max-w-lg mx-auto rounded-t-2xl p-6" onClick={e => { e.stopPropagation(); }}>
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900">{CONDITIONS[selectedCondition.key]}</h3>
                <span className="text-2xl font-bold" style={{ color: getSeverity(selectedCondition.data.percentage).color }}>
                  {selectedCondition.data.percentage}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full mb-4">
                <div className="h-full rounded-full" style={{ width: `${selectedCondition.data.percentage}%`, backgroundColor: getSeverity(selectedCondition.data.percentage).color }} />
              </div>
              <p className="text-gray-600 text-sm mb-6">{selectedCondition.data.description || doctor.analysisConsultFallback}</p>
              <button onClick={() => setSelectedCondition(null)} className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium">
                Close
              </button>
            </div>
          </div>
        ) : null}

        <div style={{ position: 'fixed', left: '-9999px', top: 0, width: '794px', zIndex: -1 }}>
          <UnifiedPDFPreview
            ref={pdfRef}
            config={doctor}
            analysisData={state.result}
            patientData={appState.patientData}
            capturedImage={appState.capturedImage}
            annotatedImage={state.annotatedImage || appState.capturedImage?.dataUrl}
          />
        </div>

        {pdf.generating ? (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 text-center">
              <div
                className="w-8 h-8 mx-auto mb-3 border-2 rounded-full animate-spin"
                style={{ borderColor: withAlpha(accent, '33'), borderTopColor: accent }}
              />
              <p className="text-gray-600">Creating PDF...</p>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return null;
};

export default UnifiedDoctorAnalysis;
